import { useEffect, useRef, useState, useCallback } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw";
import "leaflet-draw/dist/leaflet.draw.css";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Save, Trash2, MapPin, Layers, Target, Square, Circle, Pentagon, Pencil } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

declare module "leaflet" {
  namespace Control {
    class Draw extends L.Control {
      constructor(options?: any);
    }
  }
  namespace Draw {
    namespace Event {
      const CREATED: string;
      const EDITED: string;
      const DELETED: string;
    }
  }
}

export interface GeoFenceData {
  type: "Feature";
  geometry: {
    type: "Polygon" | "Circle";
    coordinates: number[] | number[][] | number[][][];
    radius?: number;
  };
  properties: {
    name?: string;
    shapeType?: string;
  };
}

interface GeofenceMapProps {
  initialGeofence?: GeoFenceData | null;
  centerLat?: number | string | null;
  centerLng?: number | string | null;
  onSave?: (geofence: GeoFenceData, center: { lat: number; lng: number }) => void;
  readOnly?: boolean;
  height?: string;
  tomtomApiKey: string;
}

const TOMTOM_TILE_URL = (apiKey: string) => 
  `https://api.tomtom.com/map/1/tile/basic/main/{z}/{x}/{y}.png?key=${apiKey}&tileSize=256`;

const defaultCenter = { lat: -25.7461, lng: 28.1881 };

export function GeofenceMap({
  initialGeofence,
  centerLat,
  centerLng,
  onSave,
  readOnly = false,
  height = "400px",
  tomtomApiKey,
}: GeofenceMapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const drawnItemsRef = useRef<L.FeatureGroup>(new L.FeatureGroup());
  const drawControlRef = useRef<L.Control.Draw | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [geofenceName, setGeofenceName] = useState("");
  const [currentShape, setCurrentShape] = useState<string | null>(null);
  const { toast } = useToast();

  const getCenter = useCallback(() => {
    if (centerLat && centerLng) {
      return {
        lat: typeof centerLat === "string" ? parseFloat(centerLat) : centerLat,
        lng: typeof centerLng === "string" ? parseFloat(centerLng) : centerLng,
      };
    }
    return defaultCenter;
  }, [centerLat, centerLng]);

  const getShapeType = (layer: L.Layer): string => {
    if (layer instanceof L.Circle) return "circle";
    if (layer instanceof L.Rectangle) return "rectangle";
    if (layer instanceof L.Polygon) return "polygon";
    return "unknown";
  };

  const layerToGeoJSON = (layer: L.Layer): GeoFenceData | null => {
    if (layer instanceof L.Circle) {
      const center = layer.getLatLng();
      const radius = layer.getRadius();
      return {
        type: "Feature",
        geometry: {
          type: "Circle",
          coordinates: [center.lng, center.lat],
          radius: radius,
        },
        properties: {
          name: geofenceName || "Geofence",
          shapeType: "circle",
        },
      };
    } else if (layer instanceof L.Polygon || layer instanceof L.Rectangle) {
      const geoJson = layer.toGeoJSON() as GeoJSON.Feature<GeoJSON.Polygon>;
      return {
        type: "Feature",
        geometry: {
          type: "Polygon",
          coordinates: geoJson.geometry.coordinates,
        },
        properties: {
          name: geofenceName || "Geofence",
          shapeType: layer instanceof L.Rectangle ? "rectangle" : "polygon",
        },
      };
    }
    return null;
  };

  const loadGeofenceFromData = useCallback((map: L.Map, geofence: GeoFenceData) => {
    drawnItemsRef.current.clearLayers();

    if (!geofence || !geofence.geometry) return;

    const { geometry, properties } = geofence;
    setGeofenceName(properties?.name || "");
    setCurrentShape(properties?.shapeType || null);

    try {
      if (geometry.type === "Circle" && geometry.radius) {
        const coords = geometry.coordinates as number[];
        const circle = L.circle([coords[1], coords[0]], {
          radius: geometry.radius,
          color: "#F97316",
          fillColor: "#F97316",
          fillOpacity: 0.2,
          weight: 2,
        });
        drawnItemsRef.current.addLayer(circle);
        map.fitBounds(circle.getBounds(), { padding: [50, 50] });
      } else if (geometry.type === "Polygon") {
        const coords = (geometry.coordinates as number[][][])[0];
        const latLngs = coords.map((c) => [c[1], c[0]] as L.LatLngTuple);
        const polygon = L.polygon(latLngs, {
          color: "#F97316",
          fillColor: "#F97316",
          fillOpacity: 0.2,
          weight: 2,
        });
        drawnItemsRef.current.addLayer(polygon);
        map.fitBounds(polygon.getBounds(), { padding: [50, 50] });
      }
    } catch (error) {
      console.error("Error loading geofence:", error);
    }
  }, []);

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    const center = getCenter();
    const map = L.map(mapRef.current, {
      center: [center.lat, center.lng],
      zoom: 16,
      zoomControl: true,
    });

    L.tileLayer(TOMTOM_TILE_URL(tomtomApiKey), {
      attribution: '&copy; <a href="https://www.tomtom.com">TomTom</a>',
      maxZoom: 22,
    }).addTo(map);

    drawnItemsRef.current = new L.FeatureGroup();
    map.addLayer(drawnItemsRef.current);

    if (!readOnly) {
      const drawControl = new L.Control.Draw({
        position: "topright",
        draw: {
          polyline: false,
          marker: false,
          circlemarker: false,
          polygon: {
            allowIntersection: false,
            drawError: {
              color: "#e74c3c",
              timeout: 1000,
            },
            shapeOptions: {
              color: "#F97316",
              fillColor: "#F97316",
              fillOpacity: 0.2,
              weight: 2,
            },
          },
          rectangle: {
            shapeOptions: {
              color: "#F97316",
              fillColor: "#F97316",
              fillOpacity: 0.2,
              weight: 2,
            },
          },
          circle: {
            shapeOptions: {
              color: "#F97316",
              fillColor: "#F97316",
              fillOpacity: 0.2,
              weight: 2,
            },
          },
        },
        edit: {
          featureGroup: drawnItemsRef.current,
          remove: true,
          selectedPathOptions: {
            maintainColor: true,
            opacity: 0.3,
          },
        },
      });

      map.addControl(drawControl);
      drawControlRef.current = drawControl;

      map.on(L.Draw.Event.CREATED, (e: any) => {
        drawnItemsRef.current.clearLayers();
        const layer = e.layer;
        drawnItemsRef.current.addLayer(layer);
        setHasUnsavedChanges(true);
        setCurrentShape(getShapeType(layer));
      });

      map.on(L.Draw.Event.EDITED, () => {
        setHasUnsavedChanges(true);
      });

      map.on(L.Draw.Event.DELETED, () => {
        setHasUnsavedChanges(true);
        setCurrentShape(null);
      });
    }

    mapInstanceRef.current = map;

    if (initialGeofence) {
      loadGeofenceFromData(map, initialGeofence);
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [tomtomApiKey, readOnly, getCenter, loadGeofenceFromData, initialGeofence]);

  const handleSave = () => {
    const layers = drawnItemsRef.current.getLayers();
    if (layers.length === 0) {
      toast({
        title: "No geofence drawn",
        description: "Please draw a shape on the map first.",
        variant: "destructive",
      });
      return;
    }

    const layer = layers[0];
    const geofence = layerToGeoJSON(layer);

    if (!geofence) {
      toast({
        title: "Error",
        description: "Could not process the geofence shape.",
        variant: "destructive",
      });
      return;
    }

    let center: { lat: number; lng: number };
    if (layer instanceof L.Circle) {
      const c = layer.getLatLng();
      center = { lat: c.lat, lng: c.lng };
    } else if (layer instanceof L.Polygon || layer instanceof L.Rectangle) {
      const bounds = (layer as L.Polygon).getBounds();
      const c = bounds.getCenter();
      center = { lat: c.lat, lng: c.lng };
    } else {
      center = getCenter();
    }

    geofence.properties.name = geofenceName || "Geofence";

    if (onSave) {
      onSave(geofence, center);
    }

    setHasUnsavedChanges(false);
    toast({
      title: "Geofence saved",
      description: `"${geofence.properties.name}" has been saved successfully.`,
    });
  };

  const handleClear = () => {
    drawnItemsRef.current.clearLayers();
    setHasUnsavedChanges(true);
    setCurrentShape(null);
    setGeofenceName("");
  };

  const centerOnLocation = () => {
    if (mapInstanceRef.current) {
      const center = getCenter();
      mapInstanceRef.current.setView([center.lat, center.lng], 16);
    }
  };

  return (
    <div className="space-y-4">
      {!readOnly && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-4">
              <CardTitle className="text-base font-medium flex items-center gap-2">
                <MapPin className="h-4 w-4 text-orange-500" />
                Geofence Editor
              </CardTitle>
              <div className="flex items-center gap-2 flex-wrap">
                {currentShape && (
                  <Badge variant="outline" className="bg-orange-500/10 text-orange-400 border-orange-500/30">
                    {currentShape === "polygon" && <Pentagon className="h-3 w-3 mr-1" />}
                    {currentShape === "rectangle" && <Square className="h-3 w-3 mr-1" />}
                    {currentShape === "circle" && <Circle className="h-3 w-3 mr-1" />}
                    {currentShape}
                  </Badge>
                )}
                {hasUnsavedChanges && (
                  <Badge variant="outline" className="bg-yellow-500/10 text-yellow-400 border-yellow-500/30">
                    Unsaved changes
                  </Badge>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-end gap-4">
              <div className="flex-1 space-y-2">
                <Label htmlFor="geofence-name" className="text-[hsl(187,100%,42%)]">
                  Geofence Name
                </Label>
                <Input
                  id="geofence-name"
                  placeholder="Enter a name for this geofence..."
                  value={geofenceName}
                  onChange={(e) => setGeofenceName(e.target.value)}
                  data-testid="input-geofence-name"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={centerOnLocation}
                  title="Center on location"
                  data-testid="button-center-map"
                >
                  <Target className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleClear}
                  title="Clear geofence"
                  data-testid="button-clear-geofence"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={!hasUnsavedChanges && !currentShape}
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                  data-testid="button-save-geofence"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save Geofence
                </Button>
              </div>
            </div>
            <div className="text-sm text-muted-foreground">
              <p className="flex items-center gap-2">
                <Pencil className="h-3 w-3" />
                Use the drawing tools on the map to create a polygon, rectangle, or circle geofence.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <div
        ref={mapRef}
        style={{ height, width: "100%" }}
        className="rounded-lg border border-border overflow-hidden"
        data-testid="map-geofence"
      />

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <div className="flex items-center gap-2">
          <Layers className="h-3 w-3" />
          <span>TomTom Maps</span>
        </div>
        {centerLat && centerLng && (
          <span>
            Center: {typeof centerLat === "string" ? parseFloat(centerLat).toFixed(6) : centerLat.toFixed(6)},{" "}
            {typeof centerLng === "string" ? parseFloat(centerLng).toFixed(6) : centerLng.toFixed(6)}
          </span>
        )}
      </div>
    </div>
  );
}
